import React, { useState, useEffect, useRef } from "react";
import { API } from "aws-amplify";
import { createCandidate } from "../graphql/mutations";
import { useToast } from "@chakra-ui/react";
import TagManager from "react-gtm-module";
import "./ContactUs.css";

const tagManagerArgs = {
  gtmId: "AW-696694130"
};

TagManager.initialize(tagManagerArgs);

export default function ContactForm() {
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastSubmitTime, setLastSubmitTime] = useState(0);
  const [honeypot, setHoneypot] = useState("");
  const [formState, setFormState] = useState({
    name: "",
    email: "",
    address: "",
    city: "",
    zipCode: "",
    projectDescription: "",
    phoneNumber: ""
  });
  const addressContainerRef = useRef(null);
  const autocompleteElRef = useRef(null);
  const [autocompleteReady, setAutocompleteReady] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Google Places Autocomplete (New API — PlaceAutocompleteElement)
  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;
    if (!apiKey) return;

    function initAutocomplete() {
      if (!addressContainerRef.current || autocompleteElRef.current) return;

      const PAE = window.google?.maps?.places?.PlaceAutocompleteElement;
      if (!PAE) {
        console.error("PlaceAutocompleteElement not available");
        return;
      }

      try {
        const autocomplete = new PAE({
          componentRestrictions: { country: "us" },
          types: ["address"]
        });

        autocompleteElRef.current = autocomplete;
        addressContainerRef.current.appendChild(autocomplete);
        setAutocompleteReady(true);

        autocomplete.addEventListener("gmp-select", async (event) => {
          const prediction = event.placePrediction;
          if (!prediction) return;

          try {
            const place = prediction.toPlace();
            await place.fetchFields({ fields: ["addressComponents", "formattedAddress"] });

            let streetNumber = "";
            let route = "";
            let city = "";
            let zip = "";

            if (place.addressComponents) {
              for (const component of place.addressComponents) {
                const types = component.types;
                if (types.includes("street_number")) streetNumber = component.longText;
                if (types.includes("route")) route = component.longText;
                if (types.includes("locality") || types.includes("sublocality_level_1")) city = component.longText;
                if (types.includes("postal_code")) zip = component.longText;
              }
            }

            const fullAddress = streetNumber ? `${streetNumber} ${route}` : route;

            setFormState((prev) => ({
              ...prev,
              address: fullAddress || place.formattedAddress || "",
              city: city || prev.city,
              zipCode: zip || prev.zipCode
            }));
          } catch (err) {
            console.error("Place details error:", err);
          }
        });
      } catch (err) {
        console.error("Places autocomplete init error:", err);
      }
    }

    // Load Google Maps via callback
    const callbackName = "__initGooglePlaces";
    if (window.google?.maps?.places) {
      initAutocomplete();
      return;
    }

    window[callbackName] = () => {
      delete window[callbackName];
      initAutocomplete();
    };

    if (!document.getElementById("google-maps-script")) {
      const script = document.createElement("script");
      script.id = "google-maps-script";
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=${callbackName}`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }, []);

  const handleContactFormSubmit = async (e) => {
    e.preventDefault();

    // Honeypot check — bots will fill this hidden field
    if (honeypot) return;

    // Rate limit — 30 second cooldown
    const now = Date.now();
    if (now - lastSubmitTime < 30000) {
      toast({
        title: "Please wait",
        position: "top-right",
        description: "Please wait a moment before submitting again.",
        status: "warning",
        duration: 5000,
        isClosable: true
      });
      return;
    }

    // Capture address from autocomplete element if user typed without selecting
    let currentAddress = formState.address;
    if (!currentAddress && autocompleteElRef.current) {
      try {
        const inner = autocompleteElRef.current.querySelector("input");
        if (inner) currentAddress = inner.value;
      } catch (e) { /* shadow DOM may block access */ }
    }

    const {
      name,
      email,
      city,
      zipCode,
      phoneNumber,
      projectDescription
    } = formState;
    const address = currentAddress;

    if (
      name &&
      email &&
      address &&
      city &&
      zipCode &&
      projectDescription &&
      phoneNumber
    ) {
      setIsSubmitting(true);
      setLastSubmitTime(now);
      try {
        await API.graphql({
          query: createCandidate,
          variables: {
            input: {
              name,
              email,
              address,
              city,
              zipCode,
              projectDescription,
              phoneNumber
            }
          }
        });
        setFormState({
          name: "",
          email: "",
          address: "",
          city: "",
          zipCode: "",
          projectDescription: "",
          phoneNumber: ""
        });
        // Clear autocomplete input text
        if (autocompleteElRef.current) {
          try {
            const inner = autocompleteElRef.current.querySelector("input");
            if (inner) inner.value = "";
          } catch (e) { /* ignore */ }
        }
        toast({
          title: "Thank you for submitting an inquiry!",
          position: "top-right",
          description: "We'll get in touch with you within 1-2 days",
          status: "success",
          duration: 5000,
          isClosable: true
        });
        window.gtag("event", "conversion", {
          send_to: "AW-696694130/qEDyCK-1i44YEPLqmswC"
        });
      } catch (err) {
        console.error("Form submission error:", err);
        toast({
          title: "Error",
          position: "top-right",
          description: "Something went wrong. Please try again or call us directly.",
          status: "error",
          duration: 5000,
          isClosable: true
        });
      } finally {
        setIsSubmitting(false);
      }
    } else {
      toast({
        title: "Uh-Oh 😥",
        position: "top-right",
        description: "Please verify all fields are filled out.",
        status: "error",
        duration: 5000,
        isClosable: true
      });
    }
  };

  return (
    <div className="contact-page">
      {/* Hero */}
      <section className="contact-hero">
        <div className="contact-hero-bg"></div>
        <div className="contact-hero-content">
          <span className="hero-label">GET IN TOUCH</span>
          <h1 className="hero-title">
            SCHEDULE YOUR
            <br />
            <span className="gold-text">FREE ESTIMATE</span>
          </h1>
          <p className="hero-description">
            Ready to start your project? Fill out the form below and our team 
            will get back to you within 24 hours.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="contact-form-section">
        <div className="contact-container">
          <div className="contact-grid">
            {/* Form */}
            <div className="form-wrapper">
              <form onSubmit={handleContactFormSubmit} className="contact-form">
                {/* Honeypot field — hidden from users, catches bots */}
                <input
                  type="text"
                  name="website"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  tabIndex="-1"
                  autoComplete="off"
                  style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0 }}
                />
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      required
                      value={formState.name}
                      onChange={(e) => setFormState({ ...formState, name: e.target.value })}
                      placeholder="John Smith"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      required
                      pattern="\(\d{3}\)\s?\d{3}-\d{4}"
                      title="Phone number in format (732) 000-0000"
                      value={formState.phoneNumber}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                        let formatted = digits;
                        if (digits.length > 6) {
                          formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
                        } else if (digits.length > 3) {
                          formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
                        } else if (digits.length > 0) {
                          formatted = `(${digits}`;
                        }
                        setFormState({ ...formState, phoneNumber: formatted });
                      }}
                      placeholder="(732) 000-0000"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    required
                    value={formState.email}
                    onChange={(e) => setFormState({ ...formState, email: e.target.value })}
                    placeholder="john@example.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="address">Street Address *</label>
                  <div ref={addressContainerRef} className="autocomplete-wrapper" />
                  {!autocompleteReady && (
                    <input
                      type="text"
                      id="address"
                      required
                      value={formState.address}
                      onChange={(e) => setFormState({ ...formState, address: e.target.value })}
                      placeholder="123 Main Street"
                    />
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="city">City *</label>
                    <input
                      type="text"
                      id="city"
                      required
                      value={formState.city}
                      onChange={(e) => setFormState({ ...formState, city: e.target.value })}
                      placeholder="Edison"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="zip">Zip Code *</label>
                    <input
                      type="text"
                      id="zip"
                      required
                      value={formState.zipCode}
                      onChange={(e) => setFormState({ ...formState, zipCode: e.target.value })}
                      placeholder="08837"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="description">Project Description *</label>
                  <textarea
                    id="description"
                    required
                    rows={5}
                    value={formState.projectDescription}
                    onChange={(e) => setFormState({ ...formState, projectDescription: e.target.value })}
                    placeholder="Tell us about your project... What type of work are you looking for? Any specific materials or design ideas?"
                  />
                </div>

                <button 
                  type="submit" 
                  className={`submit-btn ${isSubmitting ? 'submitting' : ''}`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'SUBMITTING...' : 'SUBMIT INQUIRY'}
                  {!isSubmitting && (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="contact-info">
              <div className="info-card">
                <h3 className="info-title">PREFER TO CALL?</h3>
                <p className="info-text">
                  Speak directly with our team. We're available Monday through 
                  Saturday, 7am - 6pm.
                </p>
                <div className="phone-numbers">
                  <a href="tel:7322050086" className="phone-link">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                    (732) 205-0086
                  </a>
                  <a href="tel:7325258118" className="phone-link">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                    (732) 525-8118
                  </a>
                </div>
              </div>

              <div className="info-card">
                <h3 className="info-title">WHAT TO EXPECT</h3>
                <div className="expect-list">
                  <div className="expect-item">
                    <span className="expect-number">01</span>
                    <div className="expect-content">
                      <h4>Quick Response</h4>
                      <p>We'll contact you within 24 hours</p>
                    </div>
                  </div>
                  <div className="expect-item">
                    <span className="expect-number">02</span>
                    <div className="expect-content">
                      <h4>On-Site Visit</h4>
                      <p>Free assessment at your property</p>
                    </div>
                  </div>
                  <div className="expect-item">
                    <span className="expect-number">03</span>
                    <div className="expect-content">
                      <h4>Detailed Quote</h4>
                      <p>Transparent pricing, no surprises</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="info-card highlight">
                <div className="badge-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  </svg>
                </div>
                <h3 className="info-title">LICENSED & INSURED</h3>
                <p className="license-number">NJ License #13VH12000800</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
